/**
 * Rules 2's extraction choice (2026-10-19). Janeks: *"When character is in
 * raid, he is not losing stuff (e.g. to handle the offline session), but when
 * character is extracting, player needs to choose what is left in the bag (what
 * items are extracted)."*
 *
 * The run keeps every find while it is out, over the bag's cap if need be.
 * Extract freezes it (the server stops replaying at that step) and this picker
 * opens: the haul as slots, two gauges for what the pick weighs and fills, and
 * Carry home. The server checks the pick again (`POST /raid/extract/confirm`);
 * nothing here is trusted, it only keeps the player from asking for a pick
 * that cannot fit.
 *
 * Built once and updated in place, like the live strip (see raid-panel.ts's
 * header): a poll every 2 s must not throw away a half-made pick.
 */
import { confirmExtract } from '../../state/raid-state';
import type {
  RaidItemInfo, RaidKeepPick, RaidLootItem, RaidRoom, RaidState,
} from '../../state/raid-state.types';
import { capacityLevel, fillPercent } from './capacity';
import { emptySlotHtml, slotGridStyle, slotHtml } from './item-slot';
import { itemIconUrl } from './quest-art';
import { escapeHtml as esc } from '../../util';

// ─── Pure: what fits, and what a pick weighs ─────────────────────────────────

/** How many of each find the player is taking. Absent means none. */
export type Picks = ReadonlyMap<string, number>;

function unitWeight(it: RaidLootItem): number {
  return it.qty > 0 ? it.weight / it.qty : 0;
}

export function pickTotals(haul: readonly RaidLootItem[], picks: Picks): { weight: number; slots: number } {
  let weight = 0;
  let slots = 0;
  for (const it of haul) {
    const q = picks.get(it.id) ?? 0;
    weight += unitWeight(it) * q;
    slots += q;
  }
  return { weight, slots };
}

/** The most of `item` that fits beside everything else already picked. */
export function maxFit(haul: readonly RaidLootItem[], picks: Picks, room: RaidRoom, item: RaidLootItem): number {
  const others = new Map(picks);
  others.delete(item.id);
  const t = pickTotals(haul, others);
  const bySlots = room.slots - t.slots;
  const u = unitWeight(item);
  // The same float slack the server allows (raid.formulas.ts chooseExtraction).
  const byWeight = u > 0 ? Math.floor((room.weight - t.weight + 1e-6) / u) : Infinity;
  return Math.max(0, Math.min(item.qty, bySlots, byWeight));
}

const RARITY_RANK: Record<string, number> = { RARE: 3, UNCOMMON: 2, COMMON: 1 };

/**
 * The starting pick: rarest first, junk last, lighter before heavier within a
 * rank, each taken as far as it fits. A suggestion only — every slot can be
 * changed before Carry home.
 */
export function autoPick(haul: readonly RaidLootItem[], room: RaidRoom): Map<string, number> {
  const order = [...haul].sort((a, b) => (
    (b.isJunk ? 0 : 10 + (RARITY_RANK[b.rarity] ?? 0)) - (a.isJunk ? 0 : 10 + (RARITY_RANK[a.rarity] ?? 0))
    || unitWeight(a) - unitWeight(b)
    || a.id.localeCompare(b.id)
  ));
  const picks = new Map<string, number>();
  for (const it of order) {
    const q = maxFit(haul, picks, room, it);
    if (q > 0) picks.set(it.id, q);
  }
  return picks;
}

export function toKeep(picks: Picks): RaidKeepPick[] {
  return [...picks].filter(([, q]) => q > 0).map(([itemId, qty]) => ({ itemId, qty }));
}

/** "1h 05m", "12m", "<1m". Real time, which is what a raid is measured in now. */
export function formatDuration(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 1) return '<1m';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// ─── The picker ──────────────────────────────────────────────────────────────

function nameOf(items: Record<string, RaidItemInfo>, key: string): string {
  return items[key]?.name ?? key.replace(/[_-]+/g, ' ');
}

function ceil1(n: number): number {
  return Math.ceil(n * 10 - 1e-9) / 10;
}

export interface ExtractPicker {
  root: HTMLElement;
  update(raid: RaidState): void;
}

export function createExtractPicker(): ExtractPicker {
  const root = document.createElement('div');
  root.className = 'bv-extract';
  root.innerHTML = '<p class="bv-raid-title">Carry home what fits</p>'
    + '<p class="bv-extract-note">The rest stays here. Pick by tapping; a stack can be split.</p>'
    + gauge('weight', 'LOAD') + gauge('slots', 'SLOTS')
    + '<div class="bv-found-head"><span class="bv-found-title">Haul</span><b class="bv-found-count"></b></div>'
    + `<div class="bv-found-grid bv-extract-grid" style="${slotGridStyle(5)}"></div>`
    + '<div class="bv-found-pick bv-extract-qty" hidden></div>'
    + '<div class="bv-act-row bv-extract-acts">'
    + '<button type="button" class="bv-act bv-act-ghost" data-x="best">Best fit</button>'
    + '<button type="button" class="bv-act bv-act-ghost" data-x="none">Leave all</button>'
    + '<button type="button" class="bv-act bv-act-go bv-wm-extract" data-x="go">Carry home</button>'
    + '</div>'
    + '<p class="bv-win-err" hidden></p>';

  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;
  const grid = $('.bv-extract-grid');
  const qtyRow = $('.bv-extract-qty');

  let latest: RaidState | null = null;
  let picks = new Map<string, number>();
  let haulSig = '';
  let selectedId: string | null = null;
  let busy = false;
  let gridSig = '';

  const haul = (): RaidLootItem[] => latest?.bag ?? [];
  const room = (): RaidRoom => latest?.room ?? { weight: 0, slots: 0 };

  const paint = (): void => {
    const raid = latest;
    if (!raid) return;
    const items = haul();
    const r = room();
    const t = pickTotals(items, picks);

    // The gauges count what was brought in too, so they read like the bag will.
    const broughtW = Math.max(0, raid.carryWeightCap - r.weight);
    const broughtS = Math.max(0, raid.carriedSlotCap - r.slots);
    setGauge('weight', broughtW + t.weight, raid.carryWeightCap, `${ceil1(broughtW + t.weight)}/${ceil1(raid.carryWeightCap)} kg`);
    setGauge('slots', broughtS + t.slots, raid.carriedSlotCap, `${broughtS + t.slots}/${raid.carriedSlotCap}`);

    const taking = [...picks.values()].filter((q) => q > 0).length;
    $('.bv-found-count').textContent = `${taking} of ${items.length} taken`;

    // Repaint the slots only when the haul, the pick or the selection changed:
    // rewriting them on every 2 s poll would drop keyboard focus.
    const sig = `${haulSig}|${[...picks].join(';')}|${selectedId ?? ''}`;
    if (sig !== gridSig) {
    gridSig = sig;
    const total = Math.max(5, Math.ceil(items.length / 5) * 5);
    const cells = items.map((it) => {
      const art = itemIconUrl(it.itemKey);
      return slotHtml(
        {
          itemId: it.id, itemKey: it.itemKey, name: nameOf(raid.items, it.itemKey), iconPath: raid.items[it.itemKey]?.iconPath ?? null, quantity: it.qty,
        },
        art ? `<img src="${esc(art)}" alt="" draggable="false">` : '',
      );
    });
    while (cells.length < total) cells.push(emptySlotHtml());
    grid.innerHTML = cells.join('');
    grid.querySelectorAll<HTMLElement>('.bv-slot-filled').forEach((el) => {
      const it = items.find((b) => b.id === el.dataset.itemId);
      if (!it) return;
      const q = picks.get(it.id) ?? 0;
      el.tabIndex = 0;
      el.setAttribute('role', 'button');
      el.setAttribute('aria-pressed', String(q > 0));
      el.setAttribute('aria-label', `${nameOf(raid.items, it.itemKey)}, ${q > 0 ? `taking ${q} of ${it.qty}` : 'leaving it'}`);
      if (it.isJunk) el.dataset.junk = 'true';
      if (q > 0) el.classList.add('bv-slot-picked');
      else el.classList.add('bv-extract-left');
      if (q > 0 && q < it.qty) el.insertAdjacentHTML('beforeend', `<span class="bv-extract-part">${q}/${it.qty}</span>`);
      if (it.id === selectedId) el.classList.add('bv-extract-sel');
    });
    }

    const sel = selectedId ? items.find((i) => i.id === selectedId) : undefined;
    if (sel && sel.qty > 1) {
      const q = picks.get(sel.id) ?? 0;
      const most = maxFit(items, picks, r, sel);
      qtyRow.hidden = false;
      qtyRow.innerHTML = `<span class="bv-found-pick-name">${esc(nameOf(raid.items, sel.itemKey))} ×${sel.qty}</span>`
        + `<button type="button" class="bv-act bv-act-ghost bv-extract-step" data-q="-1" aria-label="Take one less"${q <= 0 ? ' disabled' : ''}>−</button>`
        + `<b class="bv-extract-n" aria-live="polite">${q}</b>`
        + `<button type="button" class="bv-act bv-act-ghost bv-extract-step" data-q="1" aria-label="Take one more"${q >= most ? ' disabled' : ''}>+</button>`
        + `<span class="bv-chip bv-chip-plain"><small>Each</small><b>${ceil1(unitWeight(sel))} kg</b></span>`;
    } else {
      qtyRow.hidden = true;
      qtyRow.innerHTML = '';
    }

    const go = root.querySelector<HTMLButtonElement>('[data-x="go"]')!;
    go.disabled = busy;
    go.textContent = taking ? `Carry home (${taking})` : 'Leave it all';
  };

  function gauge(kind: string, label: string): string {
    return `<div class="bv-hud-row bv-raid-row" data-xgauge="${kind}"><span class="bv-hud-label">${label}</span>`
      + '<div class="bv-hud-bar" role="meter"><div class="bv-hud-carry-fill"></div><span class="bv-hud-inbar"></span></div></div>';
  }

  function setGauge(kind: string, used: number, max: number, text: string): void {
    const g = root.querySelector<HTMLElement>(`[data-xgauge="${kind}"]`)!;
    g.classList.remove('bv-cap-ok', 'bv-cap-warn', 'bv-cap-critical');
    g.classList.add(`bv-cap-${capacityLevel(used, max)}`);
    g.querySelector<HTMLElement>('.bv-hud-carry-fill')!.style.width = `${fillPercent(used, max)}%`;
    g.querySelector<HTMLElement>('.bv-hud-inbar')!.textContent = text;
    const bar = g.querySelector<HTMLElement>('.bv-hud-bar')!;
    bar.setAttribute('aria-label', label(kind));
    bar.setAttribute('aria-valuenow', String(Math.round(used * 10) / 10));
    bar.setAttribute('aria-valuemax', String(Math.round(max * 10) / 10));
  }
  function label(kind: string): string {
    return kind === 'weight' ? 'Weight you will carry' : 'Slots you will fill';
  }

  const toggle = (id: string): void => {
    const it = haul().find((b) => b.id === id);
    if (!it) return;
    selectedId = id;
    if ((picks.get(id) ?? 0) > 0) picks.delete(id);
    else {
      const q = maxFit(haul(), picks, room(), it);
      if (q > 0) picks.set(id, q);
      else flash('That does not fit. Leave something else first.');
    }
    paint();
  };

  grid.addEventListener('click', (e) => {
    const slot = (e.target as HTMLElement).closest<HTMLElement>('.bv-slot-filled');
    if (slot?.dataset.itemId) toggle(slot.dataset.itemId);
  });
  grid.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const slot = (e.target as HTMLElement).closest<HTMLElement>('.bv-slot-filled');
    if (!slot?.dataset.itemId) return;
    e.preventDefault();
    toggle(slot.dataset.itemId);
  });
  qtyRow.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-q]');
    const sel = haul().find((i) => i.id === selectedId);
    if (!b || !sel) return;
    const next = (picks.get(sel.id) ?? 0) + Number(b.dataset.q);
    const q = Math.max(0, Math.min(next, maxFit(haul(), picks, room(), sel)));
    if (q > 0) picks.set(sel.id, q); else picks.delete(sel.id);
    paint();
  });
  root.querySelector('.bv-extract-acts')!.addEventListener('click', (e) => {
    const x = (e.target as HTMLElement).closest<HTMLElement>('[data-x]')?.dataset.x;
    if (x === 'best') picks = autoPick(haul(), room());
    else if (x === 'none') picks = new Map();
    else if (x === 'go' && !busy) {
      busy = true;
      void confirmExtract(toKeep(picks)).catch(() => {}).finally(() => { busy = false; paint(); });
    }
    paint();
  });

  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  function flash(text: string): void {
    const err = $('.bv-win-err');
    err.hidden = false;
    err.textContent = text;
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      flashTimer = null;
      err.hidden = !latest?.error;
      err.textContent = latest?.error ?? '';
    }, 2500);
  }

  return {
    root,
    update(raid) {
      latest = raid;
      // A new haul (first open, or a reload mid-choice) starts from the best fit;
      // a poll with the same haul keeps whatever the player has picked.
      const sig = raid.bag.map((i) => `${i.id}x${i.qty}`).join(',');
      if (sig !== haulSig) {
        haulSig = sig;
        picks = autoPick(raid.bag, raid.room ?? { weight: 0, slots: 0 });
        selectedId = null;
      }
      if (!flashTimer) {
        const err = $('.bv-win-err');
        err.hidden = !raid.error;
        err.textContent = raid.error ?? '';
      }
      paint();
    },
  };
}
